import { useEffect, useState } from 'react';
import { fetchDeclarations, fetchAllLicenses, fetchRenewals, exportCsv } from '../services/api';

function alertBadge(level: string) {
  if (level === 'expired') return <span className="badge red">Expiré</span>;
  if (level === 'warning_30') return <span className="badge red">30 jours</span>;
  if (level === 'warning_90') return <span className="badge orange">90 jours</span>;
  return <span className="badge blue">OK</span>;
}

export default function LegacyReportExports() {
  const [tab, setTab] = useState<'exports' | 'calendar'>('exports');
  const [calendar, setCalendar] = useState<any[]>([]);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState('');
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    if (tab === 'calendar') {
      setCalLoading(true);
      fetchRenewals()
        .then(data => {
          const items = data?.items || data?.upcoming || (Array.isArray(data) ? data : []);
          setCalendar(items);
        })
        .catch(e => setCalError(e.message))
        .finally(() => setCalLoading(false));
    }
  }, [tab]);

  const doExport = async (type: string) => {
    setExporting(type);
    try {
      if (type === 'fpt') {
        const decs = await fetchDeclarations();
        exportCsv(Array.isArray(decs) ? decs : [], 'fpt-declarations.csv');
      } else if (type === 'licenses') {
        const lics = await fetchAllLicenses();
        exportCsv(lics, 'licences.csv');
      } else if (type === 'renewals') {
        const data = await fetchRenewals();
        const items = data?.items || data?.upcoming || (Array.isArray(data) ? data : []);
        exportCsv(items, 'renouvellements.csv');
      }
    } catch {
      // silent — exportCsv handles empty arrays
    } finally {
      setExporting('');
    }
  };

  const within90 = calendar.filter(r => r.alert_level === 'warning_90' || r.alert_level === 'warning_30').length;
  const within30 = calendar.filter(r => r.alert_level === 'warning_30').length;
  const expired = calendar.filter(r => r.alert_level === 'expired').length;

  return (
    <div className="card">
      <h3>Rapports</h3>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className={`btn ${tab === 'exports' ? 'primary' : ''}`} onClick={() => setTab('exports')}>Exports</button>
        <button className={`btn ${tab === 'calendar' ? 'primary' : ''}`} onClick={() => setTab('calendar')}>Calendrier renouvellements</button>
      </div>

      {tab === 'exports' && (
        <div style={{ marginTop: 24 }}>
          <div className="sub" style={{ marginBottom: 16 }}>Exportez vos données FPT et licences au format CSV.</div>
          <div className="grid3" style={{ gap: 16 }}>
            <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => doExport('fpt')}>
              <small>Déclarations FPT</small>
              <strong>{exporting === 'fpt' ? 'Export...' : 'CSV'}</strong>
              <div className="sub" style={{ marginTop: 8 }}>Toutes les déclarations FPT</div>
            </div>
            <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => doExport('licenses')}>
              <small>Licences</small>
              <strong>{exporting === 'licenses' ? 'Export...' : 'CSV'}</strong>
              <div className="sub" style={{ marginTop: 8 }}>Toutes les licences actives</div>
            </div>
            <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => doExport('renewals')}>
              <small>Renouvellements</small>
              <strong>{exporting === 'renewals' ? 'Export...' : 'CSV'}</strong>
              <div className="sub" style={{ marginTop: 8 }}>Calendrier des échéances</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'calendar' && (
        <div style={{ marginTop: 24 }}>
          {calError && <div className="error-msg">{calError}</div>}
          {calLoading ? <div className="loading">Chargement...</div> : (
            <>
              <div className="grid4" style={{ marginBottom: 18 }}>
                <div className="kpi"><small>Total</small><strong>{calendar.length}</strong></div>
                <div className="kpi"><small>Dans 90 jours</small><strong style={{ color: 'var(--orange, #f59e0b)' }}>{within90}</strong></div>
                <div className="kpi"><small>Dans 30 jours</small><strong style={{ color: 'var(--red, #ef4444)' }}>{within30}</strong></div>
                <div className="kpi"><small>Expirées</small><strong style={{ color: 'var(--red, #ef4444)' }}>{expired}</strong></div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Expiration</th><th>Établissement</th><th>N° licence</th><th>Catégorie</th><th>Jours</th><th>Alerte</th></tr>
                  </thead>
                  <tbody>
                    {calendar.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)' }}>Aucun renouvellement prévu</td></tr>}
                    {calendar.map((r, i) => (
                      <tr key={r.license_id || i}>
                        <td>{r.expiry_date || '–'}</td>
                        <td>{r.hotel_name || '–'}</td>
                        <td>{r.license_number || '–'}</td>
                        <td>{r.category_code || '–'}</td>
                        <td>{r.days_remaining ?? '–'}</td>
                        <td>{alertBadge(r.alert_level || '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
